import { useEffect, useState } from 'react';
import { Input } from '../../Input/Input';
import { Button } from '../../Button/Button';
import { InstrumentTypeField } from './InstrumentTypeField';
import { useFinancialInstrumentTranslation } from './i18n';
import type {
  CreateFinancialInstrumentPayload,
  FinancialInstrumentDto,
  InstrumentTypeOption,
} from './types';

export interface FinancialInstrumentFormProps {
  /** IF activo a editar, o `null` en modo crear. */
  instrument: FinancialInstrumentDto | null;
  /** `true` si el documento es editable (DRAFT) y el usuario puede capturar. */
  canEdit: boolean;
  /** `true` si el usuario tiene `change_default_financial_instrument`. */
  canChangeDefaultType: boolean;
  /** Tipos de instrumento disponibles (catálogo GAP-2). */
  instrumentTypes: InstrumentTypeOption[];
  /** El catálogo de tipos se está cargando. */
  typesLoading?: boolean;
  /** Tipo default sugerido por la condición de pago (GAP-3) — pre-puebla al crear. */
  defaultInstrumentTypeId?: string;
  /** Nombre del tipo default (GAP-3/GAP-4) para el chip read-only. */
  defaultInstrumentTypeName?: string;
  /** Guardado en vuelo (POST/PUT). */
  isSaving: boolean;
  /** Borrado en vuelo (DELETE). */
  isDeleting: boolean;
  onSubmit: (values: CreateFinancialInstrumentPayload) => void;
  onDelete: () => void;
}

interface FormState {
  instrumentTypeId: string;
  instrumentNumber: string;
  issueDate: string;
  dueDate: string;
  beneficiaryName: string;
  amount: string;
  amountAlt: string;
}

const EMPTY: FormState = {
  instrumentTypeId: '',
  instrumentNumber: '',
  issueDate: '',
  dueDate: '',
  beneficiaryName: '',
  amount: '',
  amountAlt: '',
};

/**
 * Formulario de captura/edición del Instrumento Financiero (105-17.01/02).
 *
 * 7 campos: tipo (chip o `Select` según permiso), número, fechas de emisión y
 * vencimiento, beneficiario y montos. Crea (POST) cuando no hay IF; edita (PUT)
 * cuando lo hay. Validación estructural inmediata (6 requeridos + `dueDate ≥
 * issueDate` + `amount > 0`); el backend es la autoridad final. Fuera de DRAFT o
 * sin permiso el formulario es read-only (sin botones).
 */
export function FinancialInstrumentForm({
  instrument,
  canEdit,
  canChangeDefaultType,
  instrumentTypes,
  typesLoading = false,
  defaultInstrumentTypeId,
  defaultInstrumentTypeName,
  isSaving,
  isDeleting,
  onSubmit,
  onDelete,
}: FinancialInstrumentFormProps) {
  const { t } = useFinancialInstrumentTranslation();

  const isCreate = !instrument;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState(false);

  // Re-sembrar cuando cambia el IF (carga / refresco). En modo crear, el tipo
  // arranca en el default sugerido por la condición de pago (GAP-3).
  useEffect(() => {
    if (instrument) {
      setForm({
        instrumentTypeId: instrument.instrumentTypeId,
        instrumentNumber: instrument.instrumentNumber,
        issueDate: instrument.issueDate,
        dueDate: instrument.dueDate,
        beneficiaryName: instrument.beneficiaryName,
        amount: String(instrument.amount ?? ''),
        amountAlt: instrument.amountAlt == null ? '' : String(instrument.amountAlt),
      });
    } else {
      setForm({ ...EMPTY, instrumentTypeId: defaultInstrumentTypeId ?? '' });
    }
    setTouched(false);
  }, [instrument, defaultInstrumentTypeId]);

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const amountNum = Number(form.amount);
  const amountAltNum = Number(form.amountAlt);

  const typeValid = form.instrumentTypeId.trim() !== '';
  const numberValid = form.instrumentNumber.trim() !== '';
  const issueValid = form.issueDate.trim() !== '';
  const dueValid = form.dueDate.trim() !== '';
  const beneficiaryValid = form.beneficiaryName.trim() !== '';
  const amountValid = form.amount.trim() !== '' && !Number.isNaN(amountNum) && amountNum > 0;
  // Comparación lexicográfica válida para fechas ISO (YYYY-MM-DD).
  const dueAfterIssue = !issueValid || !dueValid || form.dueDate >= form.issueDate;
  const amountAltValid =
    form.amountAlt.trim() === '' || (!Number.isNaN(amountAltNum) && amountAltNum > 0);

  const formValid =
    typeValid &&
    numberValid &&
    issueValid &&
    dueValid &&
    beneficiaryValid &&
    amountValid &&
    dueAfterIssue &&
    amountAltValid;

  function handleSubmit() {
    setTouched(true);
    if (!formValid) return;
    onSubmit({
      instrumentTypeId: form.instrumentTypeId,
      instrumentNumber: form.instrumentNumber.trim(),
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      beneficiaryName: form.beneficiaryName.trim(),
      amount: amountNum,
      amountAlt: form.amountAlt.trim() === '' ? null : amountAltNum,
    });
  }

  const ro = !canEdit;
  const busy = isSaving || isDeleting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canEdit && !busy) handleSubmit();
      }}
      className="flex flex-col gap-3"
    >
      <InstrumentTypeField
        value={form.instrumentTypeId}
        options={instrumentTypes}
        canChangeDefaultType={canChangeDefaultType && !ro}
        selectedTypeName={instrument?.instrumentTypeName ?? defaultInstrumentTypeName}
        isLoading={typesLoading}
        error={touched && !typeValid}
        disabled={ro || busy}
        onChange={(v) => set('instrumentTypeId', v)}
      />

      <Input
        label={t('financial_instrument.field.number')}
        placeholder={t('financial_instrument.field.numberPlaceholder')}
        maxLength={100}
        value={form.instrumentNumber}
        disabled={ro || busy}
        error={touched && !numberValid}
        errorMessage={touched && !numberValid ? t('financial_instrument.validation.required') : undefined}
        onChange={(e) => set('instrumentNumber', e.target.value)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label={t('financial_instrument.field.issueDate')}
          type="date"
          value={form.issueDate}
          disabled={ro || busy}
          error={touched && !issueValid}
          errorMessage={touched && !issueValid ? t('financial_instrument.validation.required') : undefined}
          onChange={(e) => set('issueDate', e.target.value)}
        />

        <Input
          label={t('financial_instrument.field.dueDate')}
          type="date"
          min={form.issueDate || undefined}
          value={form.dueDate}
          disabled={ro || busy}
          error={touched && (!dueValid || !dueAfterIssue)}
          errorMessage={
            touched && !dueValid
              ? t('financial_instrument.validation.required')
              : touched && !dueAfterIssue
                ? t('financial_instrument.validation.dueDateBeforeIssue')
                : undefined
          }
          onChange={(e) => set('dueDate', e.target.value)}
        />
      </div>

      <Input
        label={t('financial_instrument.field.beneficiary')}
        placeholder={t('financial_instrument.field.beneficiaryPlaceholder')}
        maxLength={255}
        value={form.beneficiaryName}
        disabled={ro || busy}
        error={touched && !beneficiaryValid}
        errorMessage={
          touched && !beneficiaryValid ? t('financial_instrument.validation.required') : undefined
        }
        onChange={(e) => set('beneficiaryName', e.target.value)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label={t('financial_instrument.field.amount')}
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          value={form.amount}
          disabled={ro || busy}
          error={touched && !amountValid}
          errorMessage={
            touched && !amountValid ? t('financial_instrument.validation.amountPositive') : undefined
          }
          onChange={(e) => set('amount', e.target.value)}
        />

        <Input
          label={t('financial_instrument.field.amountAlt')}
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          value={form.amountAlt}
          disabled={ro || busy}
          error={touched && !amountAltValid}
          errorMessage={
            touched && !amountAltValid ? t('financial_instrument.validation.amountPositive') : undefined
          }
          onChange={(e) => set('amountAlt', e.target.value)}
        />
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-3 mt-1">
          {!isCreate && (
            <Button
              type="plain"
              size="base"
              htmlType="button"
              onClick={(e) => {
                // SBC-3953 — defensa en profundidad: Eliminar NUNCA debe enviar un
                // formulario. `htmlType="button"` ya lo garantiza (default del kit),
                // y esto neutraliza además cualquier submit que pudiera burbujear
                // hacia un `<form>` ancestro del host. Solo abre el flujo de borrado.
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              disabled={busy}
            >
              <span className="text-red-600 dark:text-red-400">
                {t('financial_instrument.actions.delete')}
              </span>
            </Button>
          )}
          <Button
            type="default"
            size="base"
            htmlType="submit"
            disabled={busy || !formValid}
          >
            <span className="flex items-center justify-center gap-2 whitespace-nowrap">
              {isCreate
                ? t('financial_instrument.actions.create')
                : t('financial_instrument.actions.update')}
              {isSaving && (
                <svg
                  className="animate-spin h-4 w-4 text-white shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </span>
          </Button>
        </div>
      )}
    </form>
  );
}
